import { useDebouncedCallback } from 'use-debounce'

import SpecialitiesAutoComplete, { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'
import { useCreateSpecialityOptionMutation, useGetSpecialityOptionsQuery } from '@/core/services/speciality-option'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { useState } from 'react'

type SpecialitiesAutocompleteControllerProps<T extends FieldValues> = UseControllerProps<T> & {
  id: string
  name: string
  label: string
  editable: boolean
  canDelete?: boolean
}

export default function SpecialitiesAutocompleteController<T extends FieldValues>(props: SpecialitiesAutocompleteControllerProps<T>) {
  const { id, name, label, editable, canDelete, control } = props
  const {
    field,
  } = useController({ name, control })

  const [inputValue, setInputValue] = useState<string>()

  const [createSpecialityOption] = useCreateSpecialityOptionMutation()
  const { data: specialities } = useGetSpecialityOptionsQuery(inputValue ? { smartSearch: inputValue } : {}, { selectFromResult: ({ data }) => ({ data: data ?? [] })})

  const handleCreateSpecialityOption = async ({ name, code }: { name: string, code: string }) => {
    try {
      const createdSpecialityOption = await createSpecialityOption({ name, code }).unwrap()
      field.onChange([...field.value, createdSpecialityOption])
    } catch {}
  }

  const handleInputChange = useDebouncedCallback((newInputValue: string) => {
    setInputValue(newInputValue)
  }, 500)

  const handleSpecialityChange = (event: any, newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null, reason: string) => {
    if (typeof newValue === 'string' || !Array.isArray(newValue)) {
      return
    }
    if (reason === 'removeOption') {
      // Remove the speciality from the formik field
      field.onChange(newValue as SpecialityAutocompleteOption[])
      return
    }
    if (reason === 'createOption') {
      // find the string in the newValue array
      const speciality = newValue.find((speciality) => typeof speciality === 'string')
      if (!speciality) {
        return
      }
      if (typeof speciality !== 'string') return
      handleCreateSpecialityOption({
        name: speciality,
        code: 'CREATED.' + speciality.toLowerCase().replace(/ /g, '-'),
      })
      return
    }
    if (reason === 'selectOption') {
      // check wether added option contains "Add" in it
      const speciality = newValue.find((speciality) => typeof speciality !== 'string' && speciality.isNew)
      if (speciality && typeof speciality !== 'string') {
        if (!speciality.inputValue) return
        handleCreateSpecialityOption({
          name: (speciality as SpecialityAutocompleteOption).inputValue as string,
          code: 'CREATED.' + ((speciality as SpecialityAutocompleteOption).inputValue as string).toLowerCase().replace(/ /g, '-'),
        })
        return
      }
    }
    field.onChange(newValue as SpecialityAutocompleteOption[])
  }

  return (
    <SpecialitiesAutoComplete
      id={id}
      name={name}
      label={label}
      editable={editable}
      canDelete={canDelete}
      selectedSpecialities={field.value}
      specialities={specialities}
      onInputChange={handleInputChange}
      onSpecialityChange={handleSpecialityChange}
    />
  )
}
